import { usePostTransportationReportApi, usePutTransportationReportApi } from 'api/transportation_report'
import { Flex } from 'components/shared/flex'
import { CheckboxField, DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, ParseableValue, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { TransportationReport, TransportationReportForm } from 'entities/transportation_report'
import { useFetchVendorsApi } from 'api/vendor'
import { CheckboxTableRow, ExTable, ModalButton, TableBody, TableCell, TableHead, useMultipleCheckForm } from 'components/shared/widget'
import { Institution, InstitutionSearchForm } from 'entities/institution'
import { useFetchInstitutionsApi } from 'api/institution'
import { Pagination } from 'components/shared/pagination'
import { i } from 'mathjs'

type FormViewProps = {
    form: Form<TransportationReportForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const vendersApi = useFetchVendorsApi()
    const globalState = useContext(GlobalStateContext)
    const institutionCheckForm = useMultipleCheckForm<Institution>(form.object.institutions)
    const preSearchForm = useForm<InstitutionSearchForm>({})
    const searchForm = useForm<InstitutionSearchForm>({ page: 1, perPage: 50 })
    const api = useFetchInstitutionsApi(searchForm)
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST

    useEffect(() => {
        form.set({
            ...form.object,
        })
        vendersApi.execute()
        api.execute()
    }, [])

    useEffect(() => {
        institutionCheckForm.form.update((f) => {
            if (form.object.institutions) {
                f.items = form.object.institutions
            }
        })
    }, [form.object.id])

    useEffect(() => {
        const reportConfirmationUserId = form.object.reportConfirmationUserId
        if (reportConfirmationUserId) {
            form.update((f) => {
                f.reportConfirmationUserId = Number(reportConfirmationUserId)
            })
        }
    }, [form.object.reportConfirmationUserId])

    useEffectSkipFirst(() => {
        form.update((f) => {
            f.institutions = institutionCheckForm.form.object.items
            f.institutionsSheetCounts = institutionCheckForm.form.object.items.map((item) => {
                const interviewSheetCount = f.institutionsSheetCounts?.find((i) => i.institutionId === item.id)?.interviewSheetCount ?? 0
                return { institutionId: item.id, interviewSheetCount: interviewSheetCount }
            })
        })
    }, [institutionCheckForm.form.object.items])

    useEffectSkipFirst(() => {
        if (isHomeTest) {
            form.update((f) => (f.pickupDate = f.deliveryDate))
        }
    }, [form.object.deliveryDate])

    return (
        <>
            <SelectField
                form={form}
                attr="vendorId"
                apiError={apiError}
                label="業者"
                selectItems={vendersApi.response.results.map((v) => {
                    return { label: v.name, value: v.id! }
                })}
                required
                includeBlank
            />
            <InputField form={form} attr="code" apiError={apiError} label="伝票番号" onlyHankaku />
            <Flex>
                {!isHomeTest && <DateField form={form} attr="pickupDate" apiError={apiError} label="集荷・受領日" required />}
                <DateField form={form} attr="deliveryDate" apiError={apiError} label="受入日" required />
                <InputField form={form} attr="temperature" apiError={apiError} label="温度(℃)" type="number" required />
            </Flex>
            {!isHomeTest && (
                <>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ marginBottom: 20 }}>
                            {institutionCheckForm.form.object.items.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px 0', backgroundColor: '#f1f1f1' }}>
                                    送付元の医療機関を1つ以上選択してください
                                </div>
                            ) : (
                                <div>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th style={{ alignItems: 'center' }}>病院名</th>
                                                <th style={{ width: 200 }}>依頼書枚数</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {institutionCheckForm.form.object.items.map((item, index) => {
                                                return (
                                                    <tr key={'selected-institution-' + item.id}>
                                                        <td>{item.name}</td>
                                                        <td>
                                                            <InputField
                                                                type="number"
                                                                required
                                                                min={1}
                                                                attr={['institutionsSheetCounts', index, 'interviewSheetCount']}
                                                                form={form}
                                                                onChange={(e) => {
                                                                    form.update((f) => {
                                                                        f.institutionsSheetCounts![index].interviewSheetCount = Number(e?.target.value ?? '0')
                                                                    })
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                        <ModalButton
                            modalProps={{ size: 'lg' }}
                            openButton={
                                <div style={{ width: '100%' }}>
                                    <Button style={{ width: '100%' }}>送付元の医療機関を選択</Button>
                                </div>
                            }
                            modalHeader={<>送付元の医療機関を選択してください</>}
                            modalBody={
                                <>
                                    <InputField
                                        form={preSearchForm}
                                        attr="name"
                                        placeholder="病院名で検索"
                                        onPressEnter={() => {
                                            searchForm.set({ ...searchForm.object, ...preSearchForm.object })
                                        }}
                                    />
                                    <div style={{ height: 450, overflow: 'scroll' }}>
                                        <ExTable className="sticky-table">
                                            <TableHead>
                                                <CheckboxTableRow
                                                    head
                                                    allCheck
                                                    allItems={api.response.results}
                                                    multipleCheckForm={institutionCheckForm}
                                                    showEdit
                                                >
                                                    <TableCell th>医療機関名</TableCell>
                                                </CheckboxTableRow>
                                            </TableHead>
                                            <TableBody loading={api.loading}>
                                                {api.response.results.map((institution: Institution) => {
                                                    return (
                                                        <CheckboxTableRow
                                                            key={`institution-${institution.id}`}
                                                            item={institution}
                                                            multipleCheckForm={institutionCheckForm}
                                                        >
                                                            <TableCell>{institution.name}</TableCell>
                                                        </CheckboxTableRow>
                                                    )
                                                })}
                                            </TableBody>
                                        </ExTable>
                                    </div>
                                    <Pagination pageSet={api.pageSet} response={api.response} searchForm={searchForm}></Pagination>
                                </>
                            }
                        />
                    </div>
                </>
            )}
            <Flex>
                <InputField form={form} attr="spitzCount" apiError={apiError} label="スピッツ数" type="number" required min={1} />
            </Flex>
            <Flex>
                <SelectField
                    form={form}
                    attr="pickupStaffName"
                    apiError={apiError}
                    label="集荷担当者"
                    selectItems={[{ label: '未選択', value: '' }, ...globalState.users.map((u) => ({ label: u.fullName!, value: u.fullName! }))]}
                    required
                />
                <SelectField
                    form={form}
                    attr="reportConfirmationUserId"
                    apiError={apiError}
                    label="衛生検確認者"
                    selectItems={[{ label: '未選択', value: '' }, ...globalState.users.map((u) => ({ label: u.fullName!, value: u.id! }))]}
                    required
                />
            </Flex>
            <InputField form={form} attr="note" apiError={apiError} label="備考" as="textarea" />
        </>
    )
}

type NewTransportationReportFormProps = {
    onComplete?: () => void
}

export const NewTransportationReportForm = (props: NewTransportationReportFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostTransportationReportApi()
    const form = useForm<TransportationReportForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>集荷情報の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditTransportationReportFormProps = {
    transportationReport: TransportationReport
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditTransportationReportForm = (props: EditTransportationReportFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutTransportationReportApi()
    const form = useForm<TransportationReportForm>(props.transportationReport)

    useEffectSkipFirst(() => {
        form.set(props.transportationReport)
    }, [props.transportationReport])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>集荷情報の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
